// In-memory patient search for the doctor dashboard (namat#50). The old search
// read only the first 500 users (by document id) on every keystroke, so newer
// patients never appeared, and it filtered organizations on users.organizationId,
// which nothing writes. Membership lives in organizationMembers.

export interface DirectoryUser {
  id: string;
  firstNameEn?: string;
  lastNameEn?: string;
  firstNameAr?: string;
  lastNameAr?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  roles?: { role: string; isActive?: boolean }[];
}

export interface Membership {
  userId: string;
  organizationId: string;
  role: string;
}

export interface CustomerFilter {
  term: string;
  organizationId?: string; // 'all' or undefined = every organization
  organizationsByUser?: Map<string, string[]>;
}

// Arabic letters people type interchangeably, diacritics and tatweel removed.
export function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/[ً-ْـ]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/\s+/g, ' ')
    .trim();
}

export const isActiveCustomer = (user: DirectoryUser): boolean =>
  (user.roles || []).some((r) => r.role === 'customer' && r.isActive);

export function organizationsByUser(members: Membership[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const m of members) {
    if (m.role !== 'customer') continue;
    map.set(m.userId, [...(map.get(m.userId) || []), m.organizationId]);
  }
  return map;
}

const arabicName = (u: DirectoryUser) => `${u.firstNameAr || ''} ${u.lastNameAr || ''}`;
const englishName = (u: DirectoryUser) =>
  `${u.firstNameEn || u.firstName || ''} ${u.lastNameEn || u.lastName || ''}`;

function matchesTerm(user: DirectoryUser, term: string): boolean {
  const haystacks = [arabicName(user), englishName(user), user.email || '', user.phone || ''].map(normalizeForSearch);
  return haystacks.some((h) => h.includes(term));
}

export function filterCustomers(users: DirectoryUser[], filter: CustomerFilter): DirectoryUser[] {
  const term = normalizeForSearch(filter.term);
  const orgId = filter.organizationId && filter.organizationId !== 'all' ? filter.organizationId : undefined;
  if (!term && !orgId) return [];

  return users
    .filter(isActiveCustomer)
    .filter((u) => !orgId || (filter.organizationsByUser?.get(u.id) || []).includes(orgId))
    .filter((u) => !term || matchesTerm(u, term))
    .sort((a, b) =>
      normalizeForSearch(arabicName(a) || englishName(a)).localeCompare(normalizeForSearch(arabicName(b) || englishName(b)), 'ar'),
    );
}
